// src/components/TemplateSelector.tsx
import React from "react";
import "../styles/TemplateSelector.css";

interface Template {
  id: "modern" | "classic" | "minimal" | "creative" | "executive" | "academic";
  name: string;
  description: string;
  icon: string;
  colors: string;
  features: string[];
  bestFor: string;
  preview: string;
}

export const TEMPLATES: Template[] = [
  {
    id: "modern",
    name: "Modern",
    description: "Clean, contemporary design with bold colors",
    icon: "⚡",
    colors: "Blue & Clean",
    features: ["Modern layout", "Header highlighting", "Color accents", "Professional"],
    bestFor: "Tech & Creative professionals",
    preview: "Modern clean aesthetic with gradient header and organized sections",
  },
  {
    id: "classic",
    name: "Classic",
    description: "Traditional professional format with serif fonts",
    icon: "📋",
    colors: "Black & White",
    features: ["Traditional format", "Serif typography", "Formal styling", "ATS-friendly"],
    bestFor: "Corporate & Legal professionals",
    preview: "Classic layout with traditional serif fonts and formal structure",
  },
  {
    id: "minimal",
    name: "Minimal",
    description: "Simple, clean layout with minimal decoration",
    icon: "✨",
    colors: "Monochrome",
    features: ["Minimalist design", "Focus on content", "Clean typography", "Elegant"],
    bestFor: "All professionals",
    preview: "Minimalist design emphasizing clear content and white space",
  },
  {
    id: "creative",
    name: "Creative",
    description: "Modern with visual elements and accent colors",
    icon: "🎨",
    colors: "Vibrant",
    features: ["Visual elements", "Color schemes", "Modern design", "Unique layouts"],
    bestFor: "Designers & Creative roles",
    preview: "Creative layout with accent colors and modern visual elements",
  },
  {
    id: "executive",
    name: "Executive",
    description: "Sophisticated design for senior roles",
    icon: "👔",
    colors: "Professional",
    features: ["Executive styling", "Premium layout", "Elegant design", "High-end"],
    bestFor: "Senior & Executive roles",
    preview: "Sophisticated design tailored for C-level and executive positions",
  },
  {
    id: "academic",
    name: "Academic",
    description: "Structured format for research and education",
    icon: "🎓",
    colors: "Academic",
    features: ["Academic format", "Publication-ready", "Research focus", "Detailed"],
    bestFor: "Academics & Researchers",
    preview: "Academic format with research publications and achievements",
  },
];

interface TemplateSelectorProps {
  selectedTemplate: string;
  onSelect: (templateId: string) => void;
  onConfirm?: () => void;
  showConfirmButton?: boolean;
  maxColumns?: number;
}

const TemplateSelector: React.FC<TemplateSelectorProps> = ({
  selectedTemplate,
  onSelect,
  onConfirm,
  showConfirmButton = false,
  maxColumns = 3,
}) => {
  const selectedTemplateData = TEMPLATES.find((t) => t.id === selectedTemplate);

  return (
    <div className="ts-container">
      <div className="ts-header">
        <h2 className="ts-title">Choose Your Resume Template</h2>
        <p className="ts-subtitle">
          Select a template that matches your style and industry
        </p>
      </div>

      <div className={`ts-grid ts-grid-${maxColumns}`}>
        {TEMPLATES.map((template) => (
          <div
            key={template.id}
            className={`ts-card ${selectedTemplate === template.id ? "active" : ""}`}
            onClick={() => onSelect(template.id)}
          >
            {/* Template Icon */}
            <div className="ts-card-icon">{template.icon}</div>

            {/* Template Info */}
            <div className="ts-card-content">
              <h3 className="ts-card-name">{template.name}</h3>
              <p className="ts-card-description">{template.description}</p>

              {/* Features */}
              <div className="ts-features">
                {template.features.map((feature, idx) => (
                  <span key={idx} className="ts-feature-tag">
                    {feature}
                  </span>
                ))}
              </div>

              {/* Best For */}
              <p className="ts-best-for">
                <strong>Best for:</strong> {template.bestFor}
              </p>

              {/* Select Button */}
              <button
                className={`ts-select-btn ${selectedTemplate === template.id ? "selected" : ""}`}
                onClick={() => onSelect(template.id)}
              >
                {selectedTemplate === template.id ? "✓ Selected" : "Select"}
              </button>
            </div>

            {/* Preview Badge */}
            {selectedTemplate === template.id && (
              <div className="ts-selected-badge">✓</div>
            )}
          </div>
        ))}
      </div>

      {/* Selected Template Details */}
      {selectedTemplateData && (
        <div className="ts-selected-details">
          <div className="ts-selected-preview">
            <h3>Template Preview</h3>
            <div className="ts-preview-box">
              <div className={`ts-preview-content ts-preview-${selectedTemplateData.id}`}>
                <div className="ts-preview-header">
                  <h1>Your Name</h1>
                  <p>Professional | {selectedTemplateData.bestFor}</p>
                </div>
                <div className="ts-preview-section">
                  <h2>Professional Summary</h2>
                  <p>Brief overview of your professional background and goals.</p>
                </div>
                <div className="ts-preview-section">
                  <h2>Experience</h2>
                  <p>Job Title • Company Name | Selected content preview</p>
                </div>
                <div className="ts-preview-section">
                  <h2>Education</h2>
                  <p>Degree • Institution | 2024</p>
                </div>
              </div>
            </div>
          </div>

          <div className="ts-selected-info">
            <h3>About This Template</h3>
            <p className="ts-info-text">{selectedTemplateData.description}</p>

            <div className="ts-info-section">
              <h4>Key Features</h4>
              <ul className="ts-info-list">
                {selectedTemplateData.features.map((feature, idx) => (
                  <li key={idx}>✓ {feature}</li>
                ))}
              </ul>
            </div>

            <div className="ts-info-section">
              <h4>Best For</h4>
              <p className="ts-info-text">{selectedTemplateData.bestFor}</p>
            </div>

            <div className="ts-info-section">
              <h4>Color Scheme</h4>
              <p className="ts-info-text">{selectedTemplateData.colors}</p>
            </div>
          </div>
        </div>
      )}

      {showConfirmButton && (
        <div className="ts-actions">
          <button
            className="ts-btn ts-btn-confirm"
            onClick={onConfirm}
          >
            Continue with {selectedTemplateData?.name} Template
          </button>
        </div>
      )}
    </div>
  );
};

export default TemplateSelector;
